// app/utils/use-media-query.ts (or app/hooks/use-media-query.ts)
import { useState, useEffect } from 'react'
import { useHydrated } from 'remix-utils/use-hydrated'
import { mediaQueries } from './breakpoints' // Import the defined media queries

type BreakpointKey = keyof typeof mediaQueries

/**
 * Custom hook to detect if a CSS media query (defined in breakpoints.ts)
 * matches the current viewport. It's SSR-safe for Remix applications.
 *
 * @param queryKey The key of the predefined media query (e.g., 'svp', 'mvp', 'md').
 * @returns boolean - True if the media query matches, false otherwise.
 *                   Returns false on the server during SSR to prevent hydration mismatches.
 */
function useMediaQuery(queryKey: BreakpointKey): boolean {
	const hydrated = useHydrated()
	const [matches, setMatches] = useState(false)

	// Get the actual media query string from our constants
	const queryString = mediaQueries[queryKey]

	useEffect(() => {
		if (!hydrated || typeof window === 'undefined') {
			return
		}

		const mediaQueryList = window.matchMedia(queryString)

		const listener = (event: MediaQueryListEvent) => {
			setMatches(event.matches)
		}

		setMatches(mediaQueryList.matches)
		mediaQueryList.addEventListener('change', listener)

		return () => {
			mediaQueryList.removeEventListener('change', listener)
		}
	}, [queryString, hydrated]) // Dependency array includes queryString

	return matches
}

export { useMediaQuery }
